import React, { createContext, useContext, useState, useRef } from 'react';
import { downloadAndParseCSV } from '../map/dataHandlers/bulkSignalHandler';

const BulkSignalsContext = createContext();

export const useBulkSignals = () => {
    const context = useContext(BulkSignalsContext);
    if (!context) {
        throw new Error('useBulkSignals must be used within a BulkSignalsProvider');
    }
    return context;
};

export const BulkSignalsProvider = ({ children }) => {
    const loadingStates = useRef(new Map());
    const [bulkSignalsData, setBulkSignalsData] = useState({
        selectedBulkSignal: null,
        loadedData: new Map(),
        timelineData: new Map(),
        combinedStats: {
            totalPoints: 0,
            uniqueRegistrationIds: [],
            uniqueIpAddresses: [],
            timeRange: { min: null, max: null }
        },
        selectedStats: {
            totalPoints: 0,
            uniqueRegistrationIds: [],
            uniqueIpAddresses: [],
            timeRange: { min: null, max: null }
        }
    });

    const updateCombinedStats = (loadedData) => {
        const allData = Array.from(loadedData.values()).filter(Boolean);
        
        return {
            totalPoints: allData.reduce((sum, data) => {
                const stats = data?.stats || {};
                return sum + (stats.totalPoints || 0);
            }, 0),
            uniqueRegistrationIds: Array.from(new Set(
                allData
                    .filter(data => data?.stats?.uniqueRegistrationIds)
                    .flatMap(data => data.stats.uniqueRegistrationIds)
            )),
            uniqueIpAddresses: Array.from(new Set(
                allData
                    .filter(data => data?.stats?.uniqueIpAddresses)
                    .flatMap(data => data.stats.uniqueIpAddresses)
            )),
            timeRange: allData.reduce(
                (range, data) => {
                    const timeRange = data?.stats?.timeRange || {};
                    return {
                        min: range.min === null ? timeRange.min :
                            timeRange.min === null ? range.min :
                            Math.min(range.min, timeRange.min),
                        max: range.max === null ? timeRange.max :
                            timeRange.max === null ? range.max :
                            Math.max(range.max, timeRange.max)
                    };
                },
                { min: null, max: null }
            )
        };
    };

// In BulkSignalsContext.jsx
const loadBulkSignalData = async (bulkSignal, user, timeRange = {}) => {
    const cacheKey = bulkSignal._id;

    // Check if data is already loaded
    if (bulkSignalsData.loadedData.has(cacheKey)) {
        console.log(`Bulk signal ${cacheKey} is already loaded.`);
        return bulkSignalsData.loadedData.get(cacheKey).processedData;
    }

    try {
        // Set loading state
        loadingStates.current.set(cacheKey, true);
        
        const locations = bulkSignal.s3Key || bulkSignal.s3Location || bulkSignal.fileLocation;

        const { data: processedData = [], stats = {}, totalCount = 0 } = await downloadAndParseCSV(
            locations,
            user,
            500000,
            0,
            timeRange
        );

        // Update context immediately with new data
        setBulkSignalsData(prev => {
            const newLoadedData = new Map(prev.loadedData);
            newLoadedData.set(cacheKey, {
                rawData: processedData,
                processedData,
                stats: stats,
                totalCount
            });

            return {
                ...prev,
                loadedData: newLoadedData,
                timelineData: new Map(prev.timelineData).set(cacheKey, { rawData: processedData }),
                combinedStats: updateCombinedStats(newLoadedData)
            };
        });

        return processedData;
    } catch (error) {
        console.error(`Error loading bulk signal ${cacheKey}:`, error);
        return null;
    } finally {
        loadingStates.current.delete(cacheKey);
    }
};


    // Rest of the functions remain the same
    const getTimelineData = () => {
        return Array.from(bulkSignalsData.timelineData.values())
            .flatMap(data => data?.rawData || []);
    };

    const selectBulkSignal = (bulkSignalId) => {
        setBulkSignalsData(prev => {
            const selectedData = prev.loadedData.get(bulkSignalId);
            if (!selectedData) return prev;

            return {
                ...prev,
                selectedBulkSignal: bulkSignalId,
                selectedStats: selectedData.stats || prev.selectedStats
            };
        });
    };

    const clearSelection = () => {
        setBulkSignalsData(prev => ({
            ...prev,
            selectedBulkSignal: null,
            selectedStats: {
                totalPoints: 0,
                uniqueRegistrationIds: [],
                uniqueIpAddresses: [],
                timeRange: { min: null, max: null }
            }
        }));
    };

    const getAllProcessedData = () => {
        return Array.from(bulkSignalsData.loadedData.values())
            .flatMap(data => data?.processedData || []);
    };

    const getSelectedProcessedData = () => {
        if (!bulkSignalsData.selectedBulkSignal) return [];
        const selectedData = bulkSignalsData.loadedData.get(bulkSignalsData.selectedBulkSignal);
        return selectedData?.processedData || [];
    };

    const clearBulkSignalData = () => {
        setBulkSignalsData({
            selectedBulkSignal: null,
            loadedData: new Map(),
            timelineData: new Map(),
            combinedStats: {
                totalPoints: 0,
                uniqueRegistrationIds: [],
                uniqueIpAddresses: [],
                timeRange: { min: null, max: null }
            },
            selectedStats: {
                totalPoints: 0,
                uniqueRegistrationIds: [],
                uniqueIpAddresses: [],
                timeRange: { min: null, max: null }
            }
        });
    };

    return (
        <BulkSignalsContext.Provider value={{
            bulkSignalsData,
            setBulkSignalsData,
            loadBulkSignalData,
            selectBulkSignal,
            clearSelection,
            getAllProcessedData,
            getSelectedProcessedData,
            clearBulkSignalData,
            getTimelineData,
            isLoading: (id) => loadingStates.current.has(id)
        }}>
            {children}
        </BulkSignalsContext.Provider>
    );
};